import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { VehicleChargeType } from '../../types/vehicle-charge-type';

import { ApiResponse } from './models/api-response';
import { GenericItem } from './models/generic-item';
import { GenericList } from './models/generic-list';
import { MaintStopPoint } from './models/maint-stop-point';
import { MaintStopPointSearchConditions } from './models/maint-stop-point-search-conditions';

import { ServicerProvider } from './servicer';

const BASE_URL = '/v2/system/maint/stopPoints';

/**
 * [システム] [メンテナンス] ストップポイント のプロバイダー.
 */
@Injectable()
export class SystemMaintStopPointsServiceProvider {

  constructor(
    private servicer: ServicerProvider
  ) { }

  /**
   * 検索条件に一致するレコードを取得します.
   * 
   * @param searchConditions 検索条件
   * @returns Observable<GenericList<MaintStopPoint>>
   */
  findBySearchCondition(searchConditions?: MaintStopPointSearchConditions): Observable<GenericList<MaintStopPoint>> {
    return this.servicer.list(BASE_URL, searchConditions);
  }

  /**
   * IDが一致するレコードを1件取得します.
   * 一括取得APIを呼び出して、1件のみ返却します.
   * 
   * @param id ID
   * @returns Observable<GenericItem<MaintStopPoint>>
   */
  findById(id: number): Observable<GenericItem<MaintStopPoint>> {
    const url = `${BASE_URL}/bulkRead`;
    const data = [{ id: id }] as MaintStopPoint[];

    return this.servicer.postApi<MaintStopPoint[], GenericList<MaintStopPoint>>(url, data)
      .pipe(map((res) => {
        if (res.items && res.items.length === 1) {
          return { result: res.result, message: res.message, item: res.items[0] } as GenericItem<MaintStopPoint>;
        }

        throw new Error('No StopPoint');
      }));
  }

  /**
   * IDが一致するレコードを取得します.
   * 
   * @param ids IDのリスト
   * @returns Observable<GenericList<MaintStopPoint>>
   */
  findAllById(ids: number[]): Observable<GenericList<MaintStopPoint>> {
    const url = `${BASE_URL}/bulkRead`;
    const items = ids.map((id) => {
      return { id: id } as MaintStopPoint;
    });

    return this.servicer.postApi<MaintStopPoint[], GenericList<MaintStopPoint>>(url, items);
  }

  /**
   * 1件登録します.
   * 一括登録APIを呼び出して、1件のみ登録します.
   *
   * @param item データ
   * @returns Observable<GenericList<number>>
   */
  save(item: MaintStopPoint): Observable<GenericItem<number>> {
    const url = `${BASE_URL}/bulkRegist`;
    const items = [item] as MaintStopPoint[];
    let editItems = items.map((item) => {
      item.vehicleCharge = item.chargeSpotId ? VehicleChargeType.ENABLED : VehicleChargeType.DISABLED;
      return item;
    });
    return this.servicer.postApi<MaintStopPoint[], GenericList<number>>(url, editItems)
      .pipe(map((res) => {
        if (res.items && res.items.length === 1) {
          return { result: res.result, message: res.message, item: res.items[0] } as GenericItem<number>;
        }

        throw new Error('No StopPoint');
      }));;
  }

  /**
   * 複数件登録します.
   *
   * @param items データのリスト
   * @returns Observable<GenericList<number>>
   */
  saveAll(items: MaintStopPoint[]): Observable<GenericList<number>> {
    const url = `${BASE_URL}/bulkRegist`;
    let editItems = items.map((item) => {
      item.vehicleCharge = item.chargeSpotId ? VehicleChargeType.ENABLED : VehicleChargeType.DISABLED;
      return item;
    });
    return this.servicer.postApi<MaintStopPoint[], GenericList<number>>(url, editItems);
  }

  /**
   * 1件削除します.
   * 一括削除APIを呼び出して、1件のみ削除します.
   *
   * @param id ID
   * @returns Observable<ApiResponse>
   */
  delete(id: number): Observable<ApiResponse> {
    const url = `${BASE_URL}/bulkDelete`;
    const items = [{ id: id }] as MaintStopPoint[];

    return this.servicer.postApi<MaintStopPoint[], ApiResponse>(url, items);
  }

  /**
   * IDが一致するレコードを削除します.
   *
   * @param ids IDのリスト
   * @returns Observable<ApiResponse>
   */
  deleteAllById(ids: number[]): Observable<ApiResponse> {
    const url = `${BASE_URL}/bulkDelete`;
    const items = ids.map((id) => {
      return { id: id } as MaintStopPoint;
    });

    return this.servicer.postApi<MaintStopPoint[], ApiResponse>(url, items);
  }

  /**
   * 全レコードを削除します.
   *
   * @returns Observable<ApiResponse>
   */
  deleteAll(): Observable<ApiResponse> {
    const url = `${BASE_URL}/allDelete`;

    return this.servicer.postApi<void, ApiResponse>(url, null);
  }
}



// WEBPACK FOOTER //
// ./src/providers/servicer/system-maint-stop-points-service.ts